import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/images/logo.jpg';

function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openSubDropdown, setOpenSubDropdown] = useState(null);
  const location = useLocation();

  const navItems = [
    { name: 'Home', path: '/home' },
    { 
      name: 'Academy', 
      path: '/academy',
      dropdown: [
        { name: 'Kids', path: '/academy/kids' },
        { name: 'Teens & Adults', path: '/academy/teens-adults' },
        { name: 'Private Group', path: '/academy/private-group' },
        { name: 'Corporate', path: '/academy/corporate' },
        { name: 'Government & Public Sectors', path: '/academy/government' }
      ]
    },
    { 
      name: 'Organization', 
      path: '/organization',
      dropdown: [
        { name: 'About Us', path: '/organization/about' },
        { 
          name: 'Team', 
          path: '/organization/team',
          subdropdown: [
            { name: 'Founder', path: '/organization/team/founder' },
            { name: 'Trainers', path: '/organization/team/trainers' },
            { name: 'Game Masters', path: '/organization/team/game-master' }
          ]
        },
        { name: 'Vision', path: '/organization/vision' },
        { name: 'Social Projects', path: '/organization/social-projects' },
        { name: 'Press', path: '/organization/press' },
        { name: 'Partners & Clients', path: '/organization/partners-clients' }
      ]
    },
    { name: 'Shop', path: '/products' },
    { name: 'Tools', path: '/tools' },
    { name: 'Contact', path: '/contact' }
  ];

  const toggleDropdown = (itemName) => {
    setOpenDropdown(openDropdown === itemName ? null : itemName);
    setOpenSubDropdown(null);
  };

  const toggleSubDropdown = (itemName) => {
    setOpenSubDropdown(openSubDropdown === itemName ? null : itemName);
  };

  return (
    <nav className="bg-white shadow-md fixed top-0 left-0 right-0 z-50"> 
      <div className="w-full mx-auto px-2 sm:px-4 lg:px-6">
        <div className="flex justify-between items-center h-20">

          {/* Logo */}
          <div className="pl-1 sm:pl-2 lg:pl-3">
            <Link to="/" className="flex items-center">
              <img 
                src={logo}
                alt="Company Logo" 
                className="h-16 sm:h-20 w-auto transition-all duration-300 hover:opacity-90"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-12 mr-4 lg:mr-8">
            {navItems.map((item) => (
              <div 
                key={item.name}
                className="relative"
                onMouseEnter={() => item.dropdown && setOpenDropdown(item.name)}
                onMouseLeave={() => {
                  setOpenDropdown(null);
                  setOpenSubDropdown(null);
                }}
              >
                <Link
                  to={item.path}
                  className={`text-gray-900 font-medium text-lg hover:text-[#E8C400] transition-colors duration-200 flex items-center py-2 ${
                    location.pathname === item.path ? 'text-[#000000] border-b-2 border-[#E8C400]' : ''
                  }`}
                >
                  {item.name}
                  {item.dropdown && (
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </Link>

                {/* Desktop Dropdown */}
                {item.dropdown && openDropdown === item.name && (
                  <div className="absolute top-full left-0 pt-2 z-50">
                    <div className="w-56 bg-white shadow-lg rounded-lg py-2 border border-gray-100">
                      {item.dropdown.map((dropItem) => (
                        <div 
                          key={dropItem.name} 
                          className="relative"
                          onMouseEnter={() => dropItem.subdropdown && setOpenSubDropdown(dropItem.name)}
                          onMouseLeave={() => dropItem.subdropdown && setOpenSubDropdown(null)}
                        >
                          <Link
                            to={dropItem.path}
                            className="block px-4 py-2 text-gray-700 hover:bg-[#E8C400] hover:text-white transition-colors flex items-center justify-between"
                          >
                            {dropItem.name}
                            {dropItem.subdropdown && (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            )}
                          </Link>

                          {/* Desktop Sub-dropdown */}
                          {dropItem.subdropdown && openSubDropdown === dropItem.name && (
                            <>
                              {/* Invisible connecting bridge */}
                              <div className="absolute left-0 -right-1 h-full w-2 bg-transparent" />
                              
                              {/* Sub-dropdown moved closer */}
                              <div className="absolute left-full top-0 w-48 bg-white shadow-lg rounded-lg py-2 border border-gray-100 z-50">
                                {dropItem.subdropdown.map((subItem) => (
                                  <Link
                                    key={subItem.name}
                                    to={subItem.path}
                                    className="block px-4 py-2 text-gray-700 hover:bg-[#E8C400] hover:text-white transition-colors"
                                  >
                                    {subItem.name}
                                  </Link>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden text-gray-800 mr-2"
            aria-label="Toggle menu"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 py-4 bg-white shadow-inner">
            {navItems.map((item) => (
              <div key={item.name}>
                <div className="flex items-center justify-between">
                  <Link
                    to={item.path}
                    onClick={() => !item.dropdown && setIsMenuOpen(false)}
                    className={`flex-1 py-3 px-4 text-lg font-semibold hover:bg-gray-50 rounded-lg transition-colors ${
                      location.pathname === item.path 
                        ? 'text-blue-600 bg-blue-50' 
                        : 'text-gray-800'
                    }`}
                  >
                    {item.name}
                  </Link>
                  {item.dropdown && (
                    <button
                      onClick={() => toggleDropdown(item.name)}
                      className="px-4 py-3 text-gray-800"
                    >
                      <svg 
                        className={`w-5 h-5 transition-transform ${openDropdown === item.name ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Mobile Dropdown */}
                {item.dropdown && openDropdown === item.name && (
                  <div className="pl-4 mt-1">
                    {item.dropdown.map((dropItem) => (
                      <div key={dropItem.name}>
                        <div className="flex items-center justify-between">
                          <Link
                            to={dropItem.path}
                            onClick={() => !dropItem.subdropdown && setIsMenuOpen(false)}
                            className="flex-1 py-2 px-4 text-base text-gray-700 hover:bg-gray-50 rounded transition-colors"
                          >
                            {dropItem.name}
                          </Link>
                          {dropItem.subdropdown && (
                            <button
                              onClick={() => toggleSubDropdown(dropItem.name)}
                              className="px-4 py-2 text-gray-700"
                            >
                              <svg 
                                className={`w-4 h-4 transition-transform ${openSubDropdown === dropItem.name ? 'rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          )}
                        </div>

                        {/* Mobile Sub-dropdown */}
                        {dropItem.subdropdown && openSubDropdown === dropItem.name && (
                          <div className="pl-4 mt-1">
                            {dropItem.subdropdown.map((subItem) => (
                              <Link
                                key={subItem.name}
                                to={subItem.path}
                                onClick={() => setIsMenuOpen(false)}
                                className="block py-2 px-4 text-sm text-gray-600 hover:bg-gray-50 rounded transition-colors"
                              >
                                {subItem.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navigation;
